import { makeStyles } from '@mui/styles';
import Checkbox from 'apollo-react/components/Checkbox';
import Grid from 'apollo-react/components/Grid';
import Radio from 'apollo-react/components/Radio';
import RadioGroup from 'apollo-react/components/RadioGroup';
import TextField from 'apollo-react/components/TextField/TextField';
import qs from 'qs';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { getLookupValues } from '../../actions/DataValueLookupActions';
import {
  fetchAdvancedCriteriaSuccess,
  fetchEnhancedSearchResultsPage,
  makeEnhancedSearchAdvanced,
  resetSearchResults,
} from '../../actions/SearchPageActions';
import Actions from '../../components/AdvancedSearchModal/Actions';
import Criterion from '../../components/AdvancedSearchModal/Criterion';
import { DEFAULT_SELECT_VALUE } from '../../components/AdvancedSearchModal/Criterion/constants';
import Loader from '../../components/CustomLoader';
import { getViewName } from '../../components/SavedSearchesTable/selectors';
import {
  apiCriterionToPopoverCriterion,
  mapAdvancedSearchCriteria,
} from '../../components/SavedSearchesTable/utils';
import { ENHANCED_SEARCH_PAGE } from '../../constants/Urls';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getUITA } from '../../utils/commonUtils';
import { treeViewDataToOptions } from '../../utils/savedSearchUtils';

const useStyles = makeStyles({
  inlineCtr: {
    width: '100%',
    marginBottom: 8,
    display: 'flex',
    alignItems: 'baseline',
    '& > label': {
      width: 180,
    },
  },
  label: {
    color: '#444444',
    display: 'block',
    fontSize: 14,
    lineHeight: 1.5,
    transformOrigin: 'top left',
    width: 180,
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    '& > [class*=MuiTextField-root]': {
      display: 'block',
    },
  },
  loadingContainer: {
    position: 'absolute',
    top: 90,
    width: '95%',
    height: '100%',
    background: 'rgb(255,255,255,0.8)',
  },
  actionsContainer: {
    marginTop: 20,
  },
});

function AdvancedSearchCriteria({ entityName, ...popoverProps }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const searchPage = useSelector((state) => state?.searchPage);
  const searchTreeViewData = useSelector((state) => state?.searchTreeViewData);
  const viewName = useSelector((state) => getViewName(state, { entityName, ...popoverProps }));
  const loading = searchTreeViewData[entityName]?.loading;
  const initialCriteria =
    searchPage.entityNames?.length && searchPage.entityNames[0] === entityName
      ? searchPage?.advancedSearchCriteria?.map((criterion) =>
          apiCriterionToPopoverCriterion(criterion)
        )
      : null;
  const [criteria, setCriteria] = useState(searchPage?.advancedSearchCriteria);
  const searchParams = qs.parse(location.search);
  const [searchQuery, setSearchQuery] = useState(
    searchPage?.searchText || searchParams?.searchQuery
  );
  const [entityType, setEntityType] = useState('ALL');
  const [searchOption, setSearchOption] = useState(0);
  const [getCrossReferences, setGetCrossReferences] = useState(false);
  const translateString = useTranslation();
  const [criteriaColumns, setCriteriaColumns] = useState([]);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (
      (searchPage?.entityNames?.length && searchPage?.entityNames[0] !== 'ALL') ||
      searchParams?.entityType !== 'ALL'
    ) {
      const treeViewDataState =
        searchTreeViewData[
          searchPage?.entityNames[0] !== '' ? searchPage?.entityNames[0] : searchParams?.entityType
        ];
      setCriteriaColumns(
        treeViewDataState && treeViewDataState.treeViewData
          ? treeViewDataToOptions(treeViewDataState.treeViewData)
          : []
      );
    } else {
      setCriteriaColumns([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (popoverProps.open && initialCriteria?.length) {
      setCriteria(initialCriteria);
    }
  }, [popoverProps.open, initialCriteria]);

  useEffect(() => {
    if (
      searchPage?.advancedSearchCriteria?.length &&
      entityType !== searchPage?.entityNames?.length &&
      searchPage?.entityNames[0] !== entityType
    ) {
      setCriteria(
        searchPage?.advancedSearchCriteria?.map((criterion) =>
          apiCriterionToPopoverCriterion(criterion)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPage?.advancedSearchCriteria]);

  useEffect(() => {
    if (searchParams?.entityType !== entityType) {
      setEntityType(
        searchParams?.entityType && searchParams?.entityType !== 'All'
          ? searchParams?.entityType
          : 'ALL'
      );
    }

    if (searchParams?.searchQuery !== searchQuery || searchPage?.searchText !== searchQuery) {
      if (searchPage?.isAdvancedSearch) {
        setSearchQuery(searchPage?.searchText || searchParams?.searchQuery);
      } else {
        setSearchQuery('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (searchPage?.entityNames?.length && searchPage?.entityNames[0] !== entityType) {
      setEntityType(
        searchPage?.entityNames[0] && searchPage?.entityNames[0] !== 'All'
          ? searchPage?.entityNames[0]
          : 'ALL'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPage?.entityNames]);

  useEffect(() => {
    if (searchPage?.searchText !== searchQuery) {
      if (searchPage?.isAdvancedSearch) {
        setSearchQuery(searchPage?.searchText);
      } else {
        setSearchQuery('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPage?.searchText]);

  useEffect(() => {
    if (searchPage?.entityNames?.length && searchPage?.entityNames[0] !== 'ALL') {
      const treeViewDataState = searchTreeViewData[searchPage?.entityNames[0]];
      setCriteriaColumns(
        treeViewDataState && treeViewDataState.treeViewData
          ? treeViewDataToOptions(treeViewDataState.treeViewData)
          : []
      );
      setCriteria(
        treeViewDataState?.advancedSearchCriteria?.map((criterion) =>
          apiCriterionToPopoverCriterion(criterion)
        )
      );
    } else {
      setCriteria([]);
      setCriteriaColumns([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTreeViewData]);

  const handleChangeSearchOption = (e) => {
    setSearchOption(parseInt(e.target.value));
  };

  const handleChangeGetCrossReferences = (e, checked) => {
    setGetCrossReferences(checked);
  };

  const handleCriterionChange = (index) => (update) => {
    setCriteria(criteria.map((cr, i) => (i === index ? { ...cr, ...update } : cr)));
  };

  const handleLookup = (index) => (column) => {
    const [table, field] = column.split('.');
    const lookupValues = dispatch(getLookupValues(table, field));

    lookupValues.then((lookupValues) => {
      Array.isArray(lookupValues) && handleCriterionChange(index)({ lookupValues });
    });
  };

  const handleSearch = () => {
    const criteriaMaped = mapAdvancedSearchCriteria(criteria);
    if (searchQuery && searchQuery?.trim() !== '') {
      const data = {
        entityNames: entityType !== 'ALL' ? [entityType] : [],
        searchText: searchQuery || searchPage?.searchText,
        searchOption: searchOption ? searchOption : 0,
        getCrossReferences: getCrossReferences ? getCrossReferences : false,
        advancedSearchCriteria: criteriaMaped,
        page: 0,
        useContainsTable: true,
        isAdvancedSearch: true,
      };
      dispatch(resetSearchResults());
      dispatch(fetchAdvancedCriteriaSuccess(mapAdvancedSearchCriteria(criteria)));
      dispatch(fetchEnhancedSearchResultsPage(data));
      setHasError(false);
      dispatch(makeEnhancedSearchAdvanced({ hasError: false }));
      history.replace(
        `${ENHANCED_SEARCH_PAGE}?searchQuery=${searchQuery ||
          searchPage?.searchText}&entityType=${entityType}`
      );
    } else {
      setHasError(true);
      dispatch(makeEnhancedSearchAdvanced({ hasError: true }));
    }
  };
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && searchQuery && searchQuery?.trim() !== '') {
      handleSearch();
    } else {
      setHasError(false);
      dispatch(makeEnhancedSearchAdvanced({ hasError: false }));
    }
  };

  const handleCriterionDelete = (index) => () => {
    setCriteria(criteria.filter((_, i) => i !== index));
  };

  const handleAddCriterion = () =>
    setCriteria([
      ...criteria,
      {
        id: uuidv4(),
        openGroup: DEFAULT_SELECT_VALUE,
        closeGroup: DEFAULT_SELECT_VALUE,
        value: '',
        relationalOperator: 'and',
        allowEditting: true,
        enableLookup: true,
      },
    ]);

  return (
    <Grid container spacing={1} style={{ background: 'none' }}>
      <div className={classes.inlineCtr}>
        <div className={classes.label}>{translateString('Search For')}</div>
        <span className={classes.inputWrapper}>
          <TextField
            error={hasError || searchPage?.hasError}
            placeholder={translateString('Search')}
            value={searchQuery}
            onChange={({ target: { value } }) => setSearchQuery(value)}
            onKeyDown={handleKeyPress}
            inputProps={{
              style: { width: 350 },
            }}
          />
        </span>
      </div>
      <div className={classes.inlineCtr}>
        <RadioGroup
          label={translateString('Search Options')}
          aria-label="label"
          name="searchOption"
          value={searchOption}
          onChange={handleChangeSearchOption}
          row
        >
          <Radio value={0} label={translateString('Either Word')} />
          <Radio value={1} label={translateString('All Words')} />
          <Radio value={2} label={translateString('Exact Phrase')} />
        </RadioGroup>
      </div>
      <div className={classes.inlineCtr}>
        <div className={classes.label}></div>
        <div>
          <Checkbox
            label={translateString('Include Cross References')}
            checked={getCrossReferences}
            onChange={handleChangeGetCrossReferences}
          />
        </div>
      </div>
      {loading ? (
        <div className={classes.loadingContainer}>
          <Loader />
        </div>
      ) : (
        <>
          {criteria?.length ? (
            <>
              {criteria?.map((criterion, index) => (
                <Grid
                  key={`${criterion.id}${index}`}
                  item
                  xs={12}
                  ui-ta={getUITA('AdvancedSearch CriterionRow')}
                >
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item>
                      <Criterion
                        {...criterion}
                        allowEditting={true}
                        viewName={viewName}
                        columns={criteriaColumns}
                        onCriterionChange={handleCriterionChange(index)}
                        onDelete={handleCriterionDelete(index)}
                        onLookup={handleLookup(index)}
                        isOutPopover={true}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </>
          ) : (
            <></>
          )}
        </>
      )}
      <Grid item ui-ta={getUITA('AdvancedSearch Actions')} className={classes.actionsContainer}>
        <Actions
          onAddCriterion={criteria?.length ? handleAddCriterion : null}
          onCancel={popoverProps.onClose}
          onSearch={handleSearch}
        />
      </Grid>
    </Grid>
  );
}

export default AdvancedSearchCriteria;
